import { useEffect } from 'react';
import { globalShortcutsService } from '../services/globalShortcutsService';
import { useCommandRegistryStore } from '../stores/commandRegistryStore';
import useSpaceStore from '../stores/spaceStore';
import { useLocation } from 'react-router-dom';

/**
 * Hook to enable global new window creation shortcut (Cmd+Enter)
 * 
 * Creates a new window in the current space when pressed.
 * Works globally across the entire application.
 * 
 * Usage in App.tsx:
 * ```tsx
 * useGlobalNewWindowShortcut();
 * ```
 */
export const useGlobalNewWindowShortcut = () => {
  const { executeCommand } = useCommandRegistryStore();
  const location = useLocation();

  useEffect(() => {
    console.log('🎯 [useGlobalNewWindowShortcut] Hook mounted, registering Cmd+Enter...');
    
    // Register Cmd+Enter shortcut
    globalShortcutsService.register({
      id: 'global_new_window',
      key: 'Enter',
      modifiers: {
        cmd: true
      },
      description: 'Create new window',
      handler: () => {
        console.log('🚀 [useGlobalNewWindowShortcut] Handler called! Creating new window...');
        
        // Get current space ID from URL
        let spaceId = 'default';
        const pathMatch = location.pathname.match(/\/spaces\/([^/]+)/);
        if (pathMatch) {
          spaceId = pathMatch[1];
          console.log('📍 [useGlobalNewWindowShortcut] Using space from URL:', spaceId);
        } else {
          console.log('📍 [useGlobalNewWindowShortcut] Using default space');
        }
        
        // Execute the new_window_create command
        executeCommand('new_window_create', {
          title: 'New Window',
          spaceId: spaceId,
          createWindow: true,
          callback: (result: any) => {
            console.log('✅ [useGlobalNewWindowShortcut] NewWindow created:', result);
          }
        });
      }
    });

    console.log('✅ [useGlobalNewWindowShortcut] Shortcut registered successfully');

    // Cleanup on unmount
    return () => {
      console.log('🧹 [useGlobalNewWindowShortcut] Hook unmounting, unregistering...');
      globalShortcutsService.unregister('global_new_window');
    };
  }, [executeCommand, location.pathname]);
};

